import React, { useState } from 'react';
import fetchWithAuth from '../utils/fetchWithAuth';
import { BACKEND_URL } from '../config';

const TripList = ({ trips, vehicles, drivers, onEdit, onRemove, refresh, isConductor }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [photoModal, setPhotoModal] = useState({ open: false, url: '' });

  const getVehicleName = (plate) => {
    const v = vehicles.find(v => v.plate === plate);
    return v ? `${v.brand} ${v.model} (${v.plate})` : plate;
  };

  const getDriverName = (license) => {
    const d = drivers.find(d => d.license === license);
    return d ? `${d.name} (${license})` : license;
  };

  const filteredTrips = trips.filter(trip =>
    getVehicleName(trip.vehiclePlate).toLowerCase().includes(searchTerm.toLowerCase()) ||
    getDriverName(trip.driverLicense).toLowerCase().includes(searchTerm.toLowerCase()) ||
    (trip.origin || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (trip.destination || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Function to fetch the signed photo URL and show modal
  const handleShowPhoto = async (trip) => {
    if (!trip.photos || !trip.photos.length) return;
    // Only one photo per trip (as per your latest requirements)
    const fileName = typeof trip.photos[0] === 'string' ? trip.photos[0] : trip.photos[0].fileName;
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/photos/get-signed-url?fileName=${encodeURIComponent(fileName)}`);
      const result = await res.json();
      if (result.file && result.file.url) {
        setPhotoModal({ open: true, url: result.file.url });
      } else {
        setPhotoModal({ open: true, url: '', error: 'No se pudo obtener la foto.' });
      }
    } catch (err) {
      setPhotoModal({ open: true, url: '', error: 'No se pudo obtener la foto.' });
    }
  };

  // If conductor, show cards instead of table
  if (isConductor) {
    return (
      <div>
        <input
          type="text"
          placeholder="Buscar viaje..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="mb-4 px-3 py-2 border border-gray-300 rounded-md"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTrips.map(trip => (
            <div key={trip.id} className="relative bg-white rounded-xl shadow-md p-6 flex flex-col">
              <div className="mb-2">
                <div className="font-semibold text-lg mb-1">
                  Vehículo: <span className="font-normal">{getVehicleName(trip.vehiclePlate)}</span>
                </div>
                <div className="text-gray-700 text-sm mb-1">
                  <span className="font-medium">Conductor:</span> {getDriverName(trip.driverLicense)}
                </div>
                <div className="text-gray-700 text-sm mb-1">
                  <span className="font-medium">Fecha:</span> {trip.date}
                </div>
                <div className="text-gray-700 text-sm mb-1">
                  <span className="font-medium">Origen:</span> {trip.origin}
                </div>
                <div className="text-gray-700 text-sm mb-1">
                  <span className="font-medium">Destino:</span> {trip.destination}
                </div>
                <div className="text-gray-700 text-sm mb-1">
                  <span className="font-medium">Km inicio:</span> {trip.startKm}
                </div>
                <div className="text-gray-700 text-sm mb-1">
                  <span className="font-medium">Km fin:</span> {trip.endKm}
                </div>
                <div className="text-gray-700 text-sm mb-1">
                  <span className="font-medium">Combustible Usado (galones):</span> {trip.gasGallons}
                </div>
                <div className="text-gray-700 text-sm mb-1">
                  <span className="font-medium">Costo (S/):</span> {trip.cost}
                </div>
                <div className="text-gray-700 text-sm mb-1">
                  <span className="font-medium">Motivo:</span> {trip.purpose}
                </div>
                <div className="text-gray-700 text-sm mb-1">
                  <span className="font-medium">Foto:</span>
                  {trip.photos && trip.photos.length > 0 ? (
                    <button
                      className="ml-2 px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs"
                      onClick={() => handleShowPhoto(trip)}
                      type="button"
                    >
                      Ver foto
                    </button>
                  ) : (
                    <span className="ml-2 text-gray-400">No disponible</span>
                  )}
                </div>
              </div>
              <div className="mt-2 flex justify-end">
                <button
                  className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                  onClick={() => onEdit(trip)}
                >
                  Editar
                </button>
              </div>
            </div>
          ))}
          {filteredTrips.length === 0 && (
            <div className="col-span-full text-center text-gray-500 py-8">
              No hay viajes para mostrar.
            </div>
          )}
        </div>
        {/* Modal for photo preview */}
        {photoModal.open && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
            onClick={() => setPhotoModal({ open: false, url: '' })}
          >
            <div
              className="bg-white rounded-lg shadow-xl p-2 relative max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute -top-2 -right-2 text-white bg-gray-800 rounded-full h-8 w-8 flex items-center justify-center text-lg hover:bg-black z-10"
                onClick={() => setPhotoModal({ open: false, url: '' })}
              >&times;</button>
              {photoModal.url ? (
                <img src={photoModal.url} alt="Foto del viaje" className="w-full h-auto max-h-[85vh] object-contain rounded" />
              ) : (
                <div className="text-red-600 p-8 text-center">{photoModal.error || "No se pudo cargar la foto."}</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Default: show table for non-conductor roles
  return (
    <div>
      <input
        type="text"
        placeholder="Buscar viaje..."
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        className="mb-4 px-3 py-2 border border-gray-300 rounded-md"
      />
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded shadow">
          <thead>
            <tr>
              {/* <th className="px-4 py-2">ID</th> ID hidden but still used in code */}
              <th className="px-4 py-2">Vehículo</th>
              <th className="px-4 py-2">Fecha</th>
              <th className="px-4 py-2">Conductor</th>
              <th className="px-4 py-2">Origen</th>
              <th className="px-4 py-2">Destino</th>
              <th className="px-4 py-2">Km inicio</th>
              <th className="px-4 py-2">Km fin</th>
              <th className="px-4 py-2">Combustible Usado (galones)</th>
              <th className="px-4 py-2">Costo (S/)</th>
              <th className="px-4 py-2">Motivo</th>
              <th className="px-4 py-2">Foto</th>
              <th className="px-4 py-2">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredTrips.map(trip => (
              <tr key={trip.id}>
                {/* <td className="px-4 py-2">{trip.id}</td> ID hidden */}
                <td className="px-4 py-2">{getVehicleName(trip.vehiclePlate)}</td>
                <td className="px-4 py-2">{trip.date}</td>
                <td className="px-4 py-2">{getDriverName(trip.driverLicense)}</td>
                <td className="px-4 py-2">{trip.origin}</td>
                <td className="px-4 py-2">{trip.destination}</td>
                <td className="px-4 py-2">{trip.startKm}</td>
                <td className="px-4 py-2">{trip.endKm}</td>
                <td className="px-4 py-2">{trip.gasGallons}</td>
                <td className="px-4 py-2">{trip.cost}</td>
                <td className="px-4 py-2">{trip.purpose}</td>
                <td className="px-4 py-2">
                  {trip.photos && trip.photos.length > 0 ? (
                    <button
                      className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs"
                      onClick={() => handleShowPhoto(trip)}
                      type="button"
                    >
                      Ver foto
                    </button>
                  ) : (
                    <span className="text-gray-400">No disponible</span>
                  )}
                </td>
                <td className="px-4 py-2">
                  <button
                    className="px-2 py-1 bg-yellow-500 text-white rounded mr-2"
                    onClick={() => onEdit(trip)}
                  >
                    Editar
                  </button>
                  {onRemove && (
                    <button
                      className="px-2 py-1 bg-red-500 text-white rounded"
                      onClick={() => onRemove(trip.id)}
                    >
                      Eliminar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Modal for photo preview */}
      {photoModal.open && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setPhotoModal({ open: false, url: '' })}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-2 relative max-w-4xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute -top-2 -right-2 text-white bg-gray-800 rounded-full h-8 w-8 flex items-center justify-center text-lg hover:bg-black z-10"
              onClick={() => setPhotoModal({ open: false, url: '' })}
            >&times;</button>
            {photoModal.url ? (
              <img src={photoModal.url} alt="Foto del viaje" className="w-full h-auto max-h-[85vh] object-contain rounded" />
            ) : (
              <div className="text-red-600 p-8 text-center">{photoModal.error || "No se pudo cargar la foto."}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TripList;